import React from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";

import { CHAT_PROMPT_CUSTOM_TYPE } from "../constants/chatConstants";
import type { PromptCardProps } from "../types/chat.types";

const PromptCard: React.FC<PromptCardProps> = ({
  prompt,
  disabled = false,
  disabledReason,
  setupReason,
  onClick,
}) => {
  const { t } = useTranslation();
  const isCustom = prompt.promptType === CHAT_PROMPT_CUSTOM_TYPE;
  const descriptionId = `prompt-card-${prompt.promptType}-desc`;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-describedby={descriptionId}
      title={disabled ? disabledReason : undefined}
      className={`group relative flex h-full w-full flex-col rounded-xl border bg-white p-3 text-left transition-colors ${
        disabled
          ? "cursor-not-allowed border-gray-200 opacity-60"
          : isCustom
            ? "border-dashed border-blue-300 hover:border-blue-400 hover:bg-blue-50"
            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
      }`}
    >
      <div className="mb-1 flex items-center gap-1.5">
        <span
          className={`inline-flex h-5 w-5 items-center justify-center rounded-md ${
            isCustom ? "bg-blue-50 text-blue-600" : "bg-gray-100 text-gray-600"
          }`}
          aria-hidden="true"
        >
          <Sparkles className="h-3 w-3" />
        </span>
        <span className="text-sm font-semibold text-gray-900">{prompt.name}</span>
      </div>
      <span id={descriptionId} className="line-clamp-3 text-xs text-gray-500">
        {prompt.description}
      </span>
      {disabled && disabledReason ? (
        <span className="mt-2 text-[11px] italic text-amber-700">{disabledReason}</span>
      ) : null}
      {!disabled && setupReason ? (
        <span className="mt-2 text-[11px] text-amber-700">{setupReason}</span>
      ) : null}
      {!disabled && !setupReason && prompt.contextHonestyBadge ? (
        <span className="mt-2 text-[11px] text-gray-500">{prompt.contextHonestyBadge}</span>
      ) : null}
      {prompt.categories?.length ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {prompt.categories.map((c) => (
            <span
              key={c}
              className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-500"
            >
              {t(c)}
            </span>
          ))}
        </div>
      ) : null}
    </button>
  );
};

export default PromptCard;
